import type { Session, User } from '@supabase/supabase-js'
import { create } from 'zustand'

import { supabase } from '../client'
import type { Tables } from '../database.types'

interface AuthState {
  initialize: () => Promise<void>
  initialized: boolean
  profile: null | Profile
  session: null | Session
  setProfile: (profile: null | Profile) => void
  signOut: () => Promise<void>
  user: null | User
}

type Profile = Tables<'profiles'>

// Fetch the profile OUTSIDE the onAuthStateChange callback. Supabase-js invokes
// that callback while holding the auth lock (navigator.locks); awaiting another
// Supabase call from inside it can deadlock, which would hang app boot.
async function loadProfile(userId: string) {
  const { data: profile } = await supabase.from('profiles').select('*').eq('id', userId).single()
  useAuthStore.setState({ profile })
}

export const useAuthStore = create<AuthState>((set, get) => ({
  initialize: async () => {
    if (get().initialized) return

    // Wait for INITIAL_SESSION — Supabase fires this once it has definitively
    // resolved the auth state, including after OAuth code exchange. Using
    // getSession() alone races against the code exchange on the callback page.
    // The callback MUST stay synchronous (no await) so the auth lock is released
    // promptly; profile loading happens after the lock is free.
    const session = await new Promise<null | Session>((resolve) => {
      let settled = false
      const settle = (s: null | Session) => {
        if (settled) return
        settled = true
        resolve(s)
      }

      // Safety net: never block app boot on auth forever.
      const timer = setTimeout(() => settle(get().session), 3000)

      supabase.auth.onAuthStateChange((event, session) => {
        set({ session, user: session?.user ?? null })

        if (event === 'INITIAL_SESSION') {
          clearTimeout(timer)
          settle(session)
        } else if (session?.user) {
          // Later events (SIGNED_IN, TOKEN_REFRESHED, etc.) refresh the profile.
          void loadProfile(session.user.id)
        } else {
          set({ profile: null })
        }
      })
    })

    // Resolve init only after the profile is loaded, so role guards that read
    // `profile` immediately after boot see correct data.
    if (session?.user) {
      await loadProfile(session.user.id)
    } else {
      set({ profile: null })
    }
    set({ initialized: true })
  },
  initialized: false,
  profile: null,
  session: null,

  setProfile: (profile) => set({ profile }),

  signOut: async () => {
    await supabase.auth.signOut()
    set({ profile: null, session: null, user: null })
  },

  user: null,
}))
